import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

interface PendingRestaurant {
  id: string;
  restaurantName: string;
  ownerName: string;
  email: string;
  phone: string;
  city: string;
  district: string;
  address: string;
  cuisine: string;
  priceRange: string;
  shortDesc: string;
  description: string;
  mustTry: string[] | string;
  website: string;
  instagram: string;
  openingHours: string;
  status: "pending" | "approved" | "rejected";
  appliedAt: string;
  approvedAt?: string;
  image: string;
  images: string[];
  tags: string[];
  featured: boolean;
  rating: number;
  notified?: boolean;
  lat?: string;
  lng?: string;
}

const ADMIN_EMAIL = "admin@turkiyeseyahat.com";

const ApplicationDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [app, setApp] = useState<PendingRestaurant | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" | "info" } | null>(null);
  const [sendingEmail, setSendingEmail] = useState<"approve" | "reject" | null>(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    const auth = sessionStorage.getItem("adminAuth");
    if (auth !== "true") {
      navigate("/admin");
      return;
    }
    setAuthenticated(true);
    const pending: PendingRestaurant[] = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
    const found = pending.find((p) => p.id === id);
    if (found) {
      setApp(found);
    }
  }, [id, navigate]);

  const showToast = (msg: string, type: "success" | "error" | "info") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const getMustTryArray = (mustTry: string[] | string): string[] => {
    if (Array.isArray(mustTry)) return mustTry;
    return mustTry ? mustTry.split(",").map((s) => s.trim()).filter(Boolean) : [];
  };

  const sendEmailNotification = (type: "approve" | "reject") => {
    if (!app) return;
    setSendingEmail(type);
    setTimeout(() => {
      const pending: PendingRestaurant[] = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
      const updated = pending.map((p) => p.id === app.id ? { ...p, notified: true } : p);
      localStorage.setItem("pendingRestaurants", JSON.stringify(updated));
      setApp((prev) => prev ? { ...prev, notified: true } : prev);
      setSendingEmail(null);

      const subject = type === "approve"
        ? `Tebrikler! "${app.restaurantName}" Başvurunuz Onaylandı`
        : `"${app.restaurantName}" Başvurunuz Hakkında Bilgi`;
      const body = type === "approve"
        ? `Sayın ${app.ownerName},\n\nRestoranınız "${app.restaurantName}" editörlerimiz tarafından incelendi ve sitemizde yayınlanmaya uygun bulundu.\n\nRestoranınız artık Türkiye Seyahat Rehberi\'nde görünmektedir.\n\nİyi çalışmalar,\nTürkiye Seyahat Rehberi Ekibi`
        : `Sayın ${app.ownerName},\n\nRestoranınız "${app.restaurantName}" için yaptığınız başvuruyu inceledik. Maalesef şu an için başvurunuzu onaylayamıyoruz.\n\nDaha fazla bilgi için bizimle iletişime geçebilirsiniz.\n\nİyi çalışmalar,\nTürkiye Seyahat Rehberi Ekibi`;

      const mailtoLink = `mailto:${app.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}&cc=${encodeURIComponent(ADMIN_EMAIL)}`;
      window.open(mailtoLink, "_blank");
      showToast(`E-posta taslağı hazırlandı — ${app.email} adresine gönderilecek`, "info");
    }, 800);
  };

  const handleApprove = () => {
    if (!app) return;
    const pending: PendingRestaurant[] = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
    const updated = pending.map((p) => p.id === app.id ? { ...p, status: "approved" as const, approvedAt: new Date().toISOString() } : p);
    localStorage.setItem("pendingRestaurants", JSON.stringify(updated));

    const approved = JSON.parse(localStorage.getItem("approvedRestaurants") || "[]");
    const newRestaurant = {
      id: app.restaurantName.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") + "-" + Date.now(),
      name: app.restaurantName,
      city: app.city,
      district: app.district,
      cuisine: app.cuisine,
      shortDesc: app.shortDesc,
      description: app.description,
      image: app.image,
      images: app.images,
      tags: app.tags,
      rating: 4.5,
      priceRange: app.priceRange,
      mustTry: getMustTryArray(app.mustTry),
      featured: false,
      address: app.address,
      phone: app.phone,
      website: app.website,
      instagram: app.instagram,
      openingHours: app.openingHours,
      ownerSubmitted: true,
      approvedAt: new Date().toISOString(),
      lat: app.lat,
      lng: app.lng,
    };
    approved.push(newRestaurant);
    localStorage.setItem("approvedRestaurants", JSON.stringify(approved));
    setApp((prev) => prev ? { ...prev, status: "approved", approvedAt: new Date().toISOString() } : prev);
    showToast(`"${app.restaurantName}" onaylandı ve listeye eklendi!`, "success");
    setTimeout(() => sendEmailNotification("approve"), 600);
  };

  const handleReject = () => {
    if (!app) return;
    const pending: PendingRestaurant[] = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
    const updated = pending.map((p) => p.id === app.id ? { ...p, status: "rejected" as const } : p);
    localStorage.setItem("pendingRestaurants", JSON.stringify(updated));
    setApp((prev) => prev ? { ...prev, status: "rejected" } : prev);
    showToast(`"${app.restaurantName}" reddedildi.`, "error");
    setTimeout(() => sendEmailNotification("reject"), 600);
  };

  const handleDelete = () => {
    if (!app) return;
    const pending: PendingRestaurant[] = JSON.parse(localStorage.getItem("pendingRestaurants") || "[]");
    const updated = pending.filter((p) => p.id !== app.id);
    localStorage.setItem("pendingRestaurants", JSON.stringify(updated));
    showToast("Başvuru silindi.", "error");
    setTimeout(() => navigate("/admin"), 1200);
  };

  const statusBadge = (status: string) => {
    if (status === "pending") return (
      <span className="flex items-center gap-1.5 bg-amber-100 text-amber-700 text-sm px-3 py-1 rounded-full font-semibold">
        <i className="ri-time-line"></i> İnceleme Bekliyor
      </span>
    );
    if (status === "approved") return (
      <span className="flex items-center gap-1.5 bg-emerald-100 text-emerald-700 text-sm px-3 py-1 rounded-full font-semibold">
        <i className="ri-checkbox-circle-line"></i> Onaylandı
      </span>
    );
    return (
      <span className="flex items-center gap-1.5 bg-rose-100 text-rose-700 text-sm px-3 py-1 rounded-full font-semibold">
        <i className="ri-close-circle-line"></i> Reddedildi
      </span>
    );
  };

  const toastColors: Record<string, string> = {
    success: "bg-emerald-500 text-white",
    error: "bg-rose-500 text-white",
    info: "bg-sky-500 text-white",
  };

  if (!authenticated) return null;

  if (!app) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-unknow-line text-stone-300 text-4xl"></i>
            </div>
            <p className="text-stone-500 font-medium mb-2">Başvuru bulunamadı</p>
            <button
              onClick={() => navigate("/admin")}
              className="text-amber-600 text-sm hover:underline cursor-pointer"
            >
              Admin paneline dön
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const allImages = app.images && app.images.length > 0 ? app.images : [app.image];
  const mustTryList = getMustTryArray(app.mustTry);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2 ${toastColors[toast.type]}`}>
          <i className={toast.type === "success" ? "ri-checkbox-circle-line" : toast.type === "error" ? "ri-close-circle-line" : "ri-mail-send-line"}></i>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <section className="pt-24 pb-8 bg-stone-900">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => navigate("/admin")}
              className="flex items-center gap-1.5 text-stone-400 hover:text-white transition-colors cursor-pointer text-sm"
            >
              <i className="ri-arrow-left-line"></i> Admin Paneli
            </button>
            <span className="text-stone-600">/</span>
            <span className="text-stone-300 text-sm">Başvuru Detayı</span>
          </div>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                {app.restaurantName}
              </h1>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <div className="flex items-center gap-1 text-stone-400 text-sm">
                  <i className="ri-map-pin-line text-amber-400"></i>
                  <span>{app.city}, {app.district}</span>
                </div>
                <div className="flex items-center gap-1 text-stone-400 text-sm">
                  <i className="ri-restaurant-line text-amber-400"></i>
                  <span>{app.cuisine}</span>
                </div>
                <span className="text-stone-600 font-bold">{app.priceRange}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {statusBadge(app.status)}
              {app.notified && (
                <span className="flex items-center gap-1 bg-stone-700 text-stone-300 text-xs px-2.5 py-1 rounded-full">
                  <i className="ri-mail-check-line"></i> Bildirim Gönderildi
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Images + Quick Info */}
          <div className="lg:col-span-1 space-y-5">
            {/* Image Gallery */}
            <div className="bg-white rounded-2xl border border-stone-100 overflow-hidden">
              <div className="relative h-56 overflow-hidden">
                <img
                  src={allImages[activeImageIdx] || app.image}
                  alt={app.restaurantName}
                  className="w-full h-full object-cover object-top"
                />
                {allImages.length > 1 && (
                  <>
                    <button
                      onClick={() => setActiveImageIdx((p) => Math.max(0, p - 1))}
                      disabled={activeImageIdx === 0}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-black/50 text-white rounded-full cursor-pointer disabled:opacity-30 hover:bg-black/70 transition-colors"
                    >
                      <i className="ri-arrow-left-s-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setActiveImageIdx((p) => Math.min(allImages.length - 1, p + 1))}
                      disabled={activeImageIdx === allImages.length - 1}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center bg-black/50 text-white rounded-full cursor-pointer disabled:opacity-30 hover:bg-black/70 transition-colors"
                    >
                      <i className="ri-arrow-right-s-line text-sm"></i>
                    </button>
                    <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
                      {activeImageIdx + 1} / {allImages.length}
                    </div>
                  </>
                )}
              </div>
              {allImages.length > 1 && (
                <div className="flex gap-1.5 p-3 overflow-x-auto">
                  {allImages.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImageIdx(i)}
                      className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all cursor-pointer
                        ${activeImageIdx === i ? "border-amber-500" : "border-transparent"}`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Contact Info */}
            <div className="bg-white rounded-2xl border border-stone-100 p-5">
              <h3 className="text-sm font-bold text-stone-800 mb-4">İletişim Bilgileri</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-stone-100 rounded-lg flex-shrink-0">
                    <i className="ri-user-line text-stone-600 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-stone-400">İşletme Sahibi</p>
                    <p className="text-sm font-semibold text-stone-800">{app.ownerName}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-stone-100 rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-stone-600 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-stone-400">E-posta</p>
                    <a href={`mailto:${app.email}`} className="text-sm font-semibold text-amber-600 hover:underline break-all">{app.email}</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-stone-100 rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-stone-600 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-stone-400">Telefon</p>
                    <a href={`tel:${app.phone}`} className="text-sm font-semibold text-stone-800">{app.phone}</a>
                  </div>
                </div>
                {app.website && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-stone-100 rounded-lg flex-shrink-0">
                      <i className="ri-global-line text-stone-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-stone-400">Website</p>
                      <a href={app.website} target="_blank" rel="nofollow noreferrer" className="text-sm font-semibold text-amber-600 hover:underline break-all">{app.website}</a>
                    </div>
                  </div>
                )}
                {app.instagram && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-stone-100 rounded-lg flex-shrink-0">
                      <i className="ri-instagram-line text-stone-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-stone-400">Instagram</p>
                      <p className="text-sm font-semibold text-stone-800">{app.instagram}</p>
                    </div>
                  </div>
                )}
                {app.openingHours && (
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-stone-100 rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-stone-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-stone-400">Çalışma Saatleri</p>
                      <p className="text-sm font-semibold text-stone-800">{app.openingHours}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-2xl border border-stone-100 p-5">
              <h3 className="text-sm font-bold text-stone-800 mb-4">Başvuru Zaman Çizelgesi</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 flex-shrink-0"></div>
                  <div>
                    <p className="text-xs font-semibold text-stone-700">Başvuru Yapıldı</p>
                    <p className="text-xs text-stone-400">
                      {new Date(app.appliedAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                </div>
                {app.status === "approved" && app.approvedAt && (
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0"></div>
                    <div>
                      <p className="text-xs font-semibold text-emerald-700">Onaylandı & Yayınlandı</p>
                      <p className="text-xs text-stone-400">
                        {new Date(app.approvedAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                )}
                {app.status === "rejected" && (
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-rose-500 mt-1.5 flex-shrink-0"></div>
                    <div>
                      <p className="text-xs font-semibold text-rose-700">Reddedildi</p>
                    </div>
                  </div>
                )}
                {app.notified && (
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-sky-500 mt-1.5 flex-shrink-0"></div>
                    <div>
                      <p className="text-xs font-semibold text-sky-700">E-posta Bildirimi Gönderildi</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Full Details */}
          <div className="lg:col-span-2 space-y-5">
            {/* Restaurant Info */}
            <div className="bg-white rounded-2xl border border-stone-100 p-6">
              <h3 className="text-base font-bold text-stone-800 mb-4">Restoran Bilgileri</h3>
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-400 mb-0.5">Şehir / İlçe</p>
                  <p className="text-sm font-semibold text-stone-800">{app.city}, {app.district}</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-400 mb-0.5">Mutfak Türü</p>
                  <p className="text-sm font-semibold text-stone-800">{app.cuisine}</p>
                </div>
                <div className="bg-stone-50 rounded-xl p-3">
                  <p className="text-xs text-stone-400 mb-0.5">Fiyat Aralığı</p>
                  <p className="text-sm font-bold text-amber-600">{app.priceRange}</p>
                </div>
                {app.lat && app.lng && (
                  <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                    <p className="text-xs text-stone-400 mb-0.5">Koordinatlar</p>
                    <p className="text-xs font-semibold text-emerald-700">{parseFloat(app.lat).toFixed(4)}, {parseFloat(app.lng).toFixed(4)}</p>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <p className="text-xs text-stone-400 mb-1">Açık Adres</p>
                <p className="text-sm text-stone-700 bg-stone-50 rounded-lg p-3">{app.address}</p>
              </div>

              <div className="mb-4">
                <p className="text-xs text-stone-400 mb-1">Kısa Açıklama</p>
                <p className="text-sm text-stone-700 bg-stone-50 rounded-lg p-3">{app.shortDesc}</p>
              </div>

              <div className="mb-4">
                <p className="text-xs text-stone-400 mb-1">Detaylı Açıklama</p>
                <p className="text-sm text-stone-600 leading-relaxed bg-stone-50 rounded-lg p-3">{app.description}</p>
              </div>

              <div>
                <p className="text-xs text-stone-400 mb-2">Mutlaka Denenecek Lezzetler</p>
                <div className="flex flex-wrap gap-1.5">
                  {mustTryList.map((item, i) => (
                    <span key={i} className="bg-amber-50 text-amber-700 text-xs px-2.5 py-1 rounded-full border border-amber-100 font-medium">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Map Preview */}
            {app.lat && app.lng && (
              <div className="bg-white rounded-2xl border border-stone-100 p-5">
                <h3 className="text-sm font-bold text-stone-800 mb-3">Harita Konumu</h3>
                <div className="rounded-xl overflow-hidden border border-stone-200" style={{ height: 220 }}>
                  <iframe
                    title="Restoran Konumu"
                    src={`https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d2000!2d${app.lng}!3d${app.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1str!2str!4v1700000099`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            )}

            {/* Email Notification */}
            <div className="bg-white rounded-2xl border border-stone-100 p-5">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center bg-stone-100 rounded-lg">
                  <i className="ri-mail-line text-stone-600 text-sm"></i>
                </div>
                <h3 className="text-sm font-bold text-stone-800">E-posta Bildirimi</h3>
                {app.notified && (
                  <span className="flex items-center gap-1 bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">
                    <i className="ri-check-line"></i> Gönderildi
                  </span>
                )}
              </div>
              <p className="text-xs text-stone-500 mb-3">
                Başvuru sahibine <strong>{app.email}</strong> adresine bildirim gönderin.
              </p>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => sendEmailNotification("approve")}
                  disabled={sendingEmail !== null}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {sendingEmail === "approve" ? <><i className="ri-loader-4-line animate-spin"></i> Hazırlanıyor...</> : <><i className="ri-mail-check-line"></i> Onay E-postası</>}
                </button>
                <button
                  onClick={() => sendEmailNotification("reject")}
                  disabled={sendingEmail !== null}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 bg-rose-50 text-rose-700 border border-rose-200 rounded-lg hover:bg-rose-100 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                >
                  {sendingEmail === "reject" ? <><i className="ri-loader-4-line animate-spin"></i> Hazırlanıyor...</> : <><i className="ri-mail-close-line"></i> Red E-postası</>}
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="bg-white rounded-2xl border border-stone-100 p-5">
              <h3 className="text-sm font-bold text-stone-800 mb-4">İşlemler</h3>
              {app.status === "pending" && (
                <div className="flex gap-3 flex-wrap">
                  <button
                    onClick={handleApprove}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-500 text-white rounded-xl text-sm font-semibold hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-checkbox-circle-line text-base"></i> Onayla & Yayınla
                  </button>
                  <button
                    onClick={handleReject}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-rose-500 text-white rounded-xl text-sm font-semibold hover:bg-rose-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-close-circle-line text-base"></i> Reddet
                  </button>
                </div>
              )}
              {app.status === "approved" && (
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
                    <i className="ri-checkbox-circle-fill text-lg"></i>
                    Bu başvuru onaylandı ve restoranlar listesine eklendi.
                  </div>
                  <button
                    onClick={handleDelete}
                    className="flex items-center gap-1.5 px-3 py-2 border border-rose-200 text-rose-600 rounded-lg text-xs hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line"></i> Kaydı Sil
                  </button>
                </div>
              )}
              {app.status === "rejected" && (
                <div className="flex gap-3 flex-wrap">
                  <button
                    onClick={handleApprove}
                    className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-checkbox-circle-line"></i> Yine de Onayla
                  </button>
                  <button
                    onClick={handleDelete}
                    className="flex items-center gap-2 px-4 py-2.5 border border-rose-200 text-rose-600 rounded-xl text-sm font-medium hover:bg-rose-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line"></i> Kaydı Sil
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ApplicationDetail;
